/*
 * เอกสาร (D3, choices 28 ต.ค. 2569): ช่องข้อมูล + เลือกภาษาทีละใบ (L-b) + วันที่ "28 ตุลาคม 2026" / "28 October 2026".
 *
 *   ช่องข้อมูล   a text on the page with `field` (src/editor/editor.ts EXTRA_PROPS): its key, label in both languages,
 *                type, required, choices, where a company's value comes from (src/docs/docProfile.ts) and its value `v`.
 *                The text shown IS the value, written for the document's language — fieldText(). Fields with the same
 *                key in a work share one value. A logo slot is a field of type 'logo' (a picture, no text).
 *   สองภาษา     a text with `i18n` {th, en, l}: its words in each language, `l` the one shown; a table cell with `tr`
 *                (src/table/tableModel.ts). Switching the language changes only words that are still the template's —
 *                words the person changed themselves are kept and counted (said, never silently overwritten).
 *
 * Plain data (no fabric, no DOM): the app and the server (worker/rules.ts checkDesign) use the same checker (BUG-13);
 * the page edits here work on saved page JSON, the same for the open page and the others (src/ui/docFill.ts).
 */
import { DOC_LANGS, PROFILE_KEYS, type DocLang, type ProfileKey } from './docProfile';

export const FIELD_TYPES = ['text', 'multiline', 'number', 'date', 'choice', 'logo'] as const;
export type FieldType = typeof FIELD_TYPES[number];
export const FIELD_TYPE_TH: Record<FieldType, string> = { text: 'ข้อความ', multiline: 'ข้อความหลายบรรทัด', number: 'ตัวเลข', date: 'วันที่', choice: 'ตัวเลือก', logo: 'โลโก้' };
export const FIELD_VALUE_MAX = 1000, FIELD_LABEL_MAX = 80, FIELD_OPTS_MAX = 20, I18N_MAX = 1000;
/** what an empty field shows (and prints: a line to write on by hand) */
export const FIELD_EMPTY = '……………………';

export interface DocField {
  /** same key = same value in the whole work */
  k: string;
  label: { th: string; en: string };
  type: FieldType;
  req?: true;
  /** choice: the same choices in both languages, in the same order */
  opts?: { th: string[]; en: string[] };
  /** filled from the chosen company/brand */
  src?: ProfileKey;
  /** the value: text / a number as typed / a date YYYY-MM-DD / a choice's index ('' or absent = empty) */
  v?: string;
  /** the language its text is written in */
  l: DocLang;
  /** logo: the slot's size on the page (w, h px) — a company's logo is fitted inside it, whatever its shape */
  box?: [number, number];
}
export interface I18nText { th: string; en: string; l: DocLang }

type Checked<T> = { ok: true; value: T } | { ok: false; error: string };
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const len = (s: string) => [...s].length;
export const FIELD_KEY = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;

/* ---------- dates: Thailand time, ค.ศ. (choice ข: the month in full) ---------- */

const MONTHS: Record<DocLang, string[]> = {
  th: ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
/** a real day written YYYY-MM-DD */
export function isIsoDay(v: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}
/** "28 ตุลาคม 2026" / "28 October 2026" */
export function formatDay(iso: string, lang: DocLang): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[lang][m - 1]} ${y}`;
}
/** today in Thailand, YYYY-MM-DD (whatever the device's time zone) */
export function thaiToday(now = new Date()): string {
  return new Date(now.getTime() + 7 * 3600_000).toISOString().slice(0, 10);
}

/* ---------- the checker ---------- */

const NUMBER = /^[+-]?(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/;

/** a field's value for its type, or why not ('' = empty, always fine here; required is said by the panel) */
export function valueProblem(f: Pick<DocField, 'type' | 'opts' | 'label'>, v: string): string | null {
  const name = `"${f.label.th || f.label.en}"`;
  if (!v) return null;
  if (len(v) > FIELD_VALUE_MAX) return `ช่อง ${name} ยาวเกิน ${FIELD_VALUE_MAX} ตัวอักษร`;
  if (f.type === 'text' && v.includes('\n')) return `ช่อง ${name} เป็นบรรทัดเดียว (ขึ้นบรรทัดใหม่ไม่ได้)`;
  if (f.type === 'number' && !NUMBER.test(v)) return `ช่อง ${name} ต้องเป็นตัวเลข เช่น 15 หรือ 1,250.50`;
  if (f.type === 'date' && !isIsoDay(v)) return `ช่อง ${name} ต้องเป็นวันที่ที่มีจริง`;
  if (f.type === 'choice' && !(/^\d+$/.test(v) && Number(v) < (f.opts?.th.length ?? 0))) return `ช่อง ${name} ต้องเลือกจากรายการ`;
  if (f.type === 'logo') return `ช่อง ${name} เป็นโลโก้ ไม่มีข้อความ`;
  return null;
}

/** a field (a clean copy: checking it again gives the same — BUG-72), or why not */
export function checkField(raw: unknown): Checked<DocField> {
  if (!isObj(raw)) return { ok: false, error: 'ช่องข้อมูลเสีย (ไม่ใช่ข้อมูลช่อง)' };
  if (typeof raw.k !== 'string' || !FIELD_KEY.test(raw.k)) return { ok: false, error: `ชื่อช่องข้อมูล "${String(raw.k ?? '')}" ไม่ถูกรูปแบบ` };
  const lb = isObj(raw.label) ? raw.label : {};
  const label = { th: typeof lb.th === 'string' ? lb.th.trim() : '', en: typeof lb.en === 'string' ? lb.en.trim() : '' };
  if (!label.th || !label.en) return { ok: false, error: `ช่อง ${raw.k} ต้องมีชื่อทั้งภาษาไทยและอังกฤษ` };
  if (len(label.th) > FIELD_LABEL_MAX || len(label.en) > FIELD_LABEL_MAX) return { ok: false, error: `ชื่อช่อง "${label.th}" ยาวเกิน ${FIELD_LABEL_MAX} ตัวอักษร` };
  if (!(FIELD_TYPES as readonly unknown[]).includes(raw.type)) return { ok: false, error: `ช่อง "${label.th}" มีชนิดที่ระบบไม่รู้จัก` };
  if (!(DOC_LANGS as readonly unknown[]).includes(raw.l)) return { ok: false, error: `ช่อง "${label.th}" ไม่ได้บอกภาษาที่แสดง` };
  const out: DocField = { k: raw.k, label, type: raw.type as FieldType, l: raw.l as DocLang };
  if (raw.req !== undefined) { if (raw.req !== true) return { ok: false, error: `ช่อง "${label.th}": ค่า "จำเป็น" เสีย` }; out.req = true; }
  if (out.type === 'choice') {
    const o = isObj(raw.opts) ? raw.opts : {};
    const th = Array.isArray(o.th) ? o.th : [], en = Array.isArray(o.en) ? o.en : [];
    if (!th.length || th.length !== en.length || th.length > FIELD_OPTS_MAX || ![...th, ...en].every(x => typeof x === 'string' && x.trim() && len(x) <= FIELD_LABEL_MAX)) {
      return { ok: false, error: `ช่องตัวเลือก "${label.th}" ต้องมีตัวเลือก 1–${FIELD_OPTS_MAX} ข้อ ทั้งสองภาษาเท่ากัน` };
    }
    out.opts = { th: th.map(x => (x as string).trim()), en: en.map(x => (x as string).trim()) };
  } else if (raw.opts !== undefined) return { ok: false, error: `ช่อง "${label.th}" ไม่ใช่ช่องตัวเลือก แต่มีรายการตัวเลือก` };
  if (raw.src !== undefined) {
    if (!(PROFILE_KEYS as readonly unknown[]).includes(raw.src)) return { ok: false, error: `ช่อง "${label.th}" ดึงข้อมูลบริษัทช่องที่ไม่มี` };
    out.src = raw.src as ProfileKey;
  }
  if (raw.box !== undefined) {
    const b = raw.box;
    if (out.type !== 'logo' || !Array.isArray(b) || b.length !== 2 || !b.every(n => typeof n === 'number' && Number.isFinite(n) && n > 0 && n <= 20000)) {
      return { ok: false, error: `ขนาดช่องโลโก้ "${label.th}" เสีย` };
    }
    out.box = [b[0], b[1]];
  }
  if (raw.v !== undefined && raw.v !== '') {
    if (typeof raw.v !== 'string') return { ok: false, error: `ค่าของช่อง "${label.th}" เสีย` };
    const why = valueProblem(out, raw.v);
    if (why) return { ok: false, error: why };
    out.v = raw.v;
  }
  return { ok: true, value: out };
}

export function checkI18n(raw: unknown): Checked<I18nText> {
  if (!isObj(raw) || typeof raw.th !== 'string' || typeof raw.en !== 'string') return { ok: false, error: 'ข้อความสองภาษาเสีย' };
  if (len(raw.th) > I18N_MAX || len(raw.en) > I18N_MAX) return { ok: false, error: `ข้อความสองภาษายาวเกิน ${I18N_MAX} ตัวอักษร` };
  if (!(DOC_LANGS as readonly unknown[]).includes(raw.l)) return { ok: false, error: 'ข้อความสองภาษาไม่ได้บอกภาษาที่แสดง' };
  return { ok: true, value: { th: raw.th, en: raw.en, l: raw.l as DocLang } };
}

/** every field and two-language text of a design (all pages, inside groups too), or the first problem with its page */
export function docProblem(design: unknown): string | null {
  if (!isObj(design)) return null;
  const pages: unknown[] = [design.objects, ...(Array.isArray(design.extraPages) ? design.extraPages.map(p => isObj(p) ? p.objects : null) : [])];
  const walk = (list: unknown, page: number): string | null => {
    if (!Array.isArray(list)) return null;
    for (const o of list) {
      if (!isObj(o)) continue;
      if (o.field !== undefined) { const k = checkField(o.field); if (!k.ok) return `ช่องข้อมูลในหน้า ${page}: ${k.error}`; }
      if (o.i18n !== undefined) { const k = checkI18n(o.i18n); if (!k.ok) return `ข้อความในหน้า ${page}: ${k.error}`; }
      const inner = walk(o.objects, page);
      if (inner) return inner;
    }
    return null;
  };
  for (const [i, list] of pages.entries()) { const why = walk(list, i + 1); if (why) return why; }
  return null;
}

/* ---------- what a field shows ---------- */

export function fieldText(f: DocField, lang: DocLang): string {
  const v = f.v ?? '';
  if (!v) return FIELD_EMPTY;
  if (f.type === 'date') return isIsoDay(v) ? formatDay(v, lang) : v;
  if (f.type === 'choice') return f.opts?.[lang][Number(v)] ?? v;
  return v;
}

/* ---------- the work's pages as saved JSON (plain objects) ---------- */

export type Plain = Record<string, unknown>;
export interface FoundField { page: number; field: DocField }

/** every field of the work, page by page in order (a logo too) */
export function fieldsOf(pages: Plain[][]): FoundField[] {
  const out: FoundField[] = [];
  pages.forEach((objs, page) => objs.forEach(o => {
    const k = o.field !== undefined ? checkField(o.field) : null;
    if (k?.ok) out.push({ page, field: k.value });
  }));
  return out;
}

/** one entry per key (the first place it is), with the value they share — a key whose places disagree is listed */
export function fieldList(pages: Plain[][]): { fields: FoundField[]; differ: string[] } {
  const seen = new Map<string, FoundField>(), differ = new Set<string>();
  for (const f of fieldsOf(pages)) {
    const first = seen.get(f.field.k);
    if (!first) seen.set(f.field.k, f);
    else if ((first.field.v ?? '') !== (f.field.v ?? '') && f.field.type !== 'logo') differ.add(f.field.label.th);
  }
  return { fields: [...seen.values()], differ: [...differ] };
}

/** the language the work is written in: what its fields and two-language texts show (th when it has none) */
export function docLangOf(pages: Plain[][]): DocLang {
  for (const objs of pages) for (const o of objs) {
    const l = (isObj(o.field) ? o.field.l : isObj(o.i18n) ? o.i18n.l : undefined);
    if (l === 'th' || l === 'en') return l;
  }
  return 'th';
}

/**
 * Values put into the fields: every place with that key gets the value and shows it (as text). `values` are checked
 * first — one wrong value changes nothing at all (CORE: no half updates). Returns how many places changed.
 */
export function fillFields(pages: Plain[][], values: Record<string, string>): Checked<{ pages: Plain[][]; changed: number }> {
  const found = new Map(fieldsOf(pages).map(f => [f.field.k, f.field] as const));
  for (const [k, v] of Object.entries(values)) {
    const f = found.get(k);
    if (!f) return { ok: false, error: `ไม่พบช่อง "${k}" ในงานนี้แล้ว (อาจถูกลบระหว่างกรอก) ลองเปิดแผงกรอกใหม่` };
    const why = valueProblem(f, v);
    if (why) return { ok: false, error: why };
  }
  let changed = 0;
  const out = pages.map(objs => objs.map(o => {
    if (o.field === undefined) return o;
    const k = checkField(o.field);
    if (!k.ok || !(k.value.k in values) || k.value.type === 'logo') return o;
    const v = values[k.value.k];
    const f: DocField = { ...k.value };
    if (v) f.v = v; else delete f.v;
    changed++;
    return { ...o, field: f, text: fieldText(f, f.l) };
  }));
  return { ok: true, value: { pages: out, changed } };
}

/**
 * The work in the other language: two-language texts, fields (their value written for it), table cells with `tr`.
 * Words changed by the person (not what the template had in either language) stay and are counted in `kept`;
 * `tables` = the table objects whose cells changed (the app draws them again — src/table/tableFabric.ts).
 */
export function switchLang(pages: Plain[][], to: DocLang): { pages: Plain[][]; changed: number; kept: number; tables: Array<{ page: number; index: number }> } {
  let changed = 0, kept = 0;
  const tables: Array<{ page: number; index: number }> = [];
  const out = pages.map((objs, page) => objs.map((o, index) => {
    if (isObj(o.i18n)) {
      const k = checkI18n(o.i18n);
      if (!k.ok || k.value.l === to) return o;
      if (o.text !== k.value[k.value.l]) { kept++; return { ...o, i18n: { ...k.value, l: to } }; }
      changed++;
      return { ...o, text: k.value[to], i18n: { ...k.value, l: to } };
    }
    if (isObj(o.field)) {
      const k = checkField(o.field);
      if (!k.ok || k.value.l === to) return o;
      const f: DocField = { ...k.value, l: to };
      if (k.value.type === 'logo') return { ...o, field: f };
      if (o.text !== fieldText(k.value, k.value.l)) { kept++; return { ...o, field: f }; }
      changed++;
      return { ...o, field: f, text: fieldText(f, to) };
    }
    if (isObj(o.table) && Array.isArray(o.table.cells)) {
      let touched = false;
      const cells = (o.table.cells as unknown[][]).map(row => row.map(c => {
        if (!isObj(c) || !isObj(c.tr) || typeof c.tr.th !== 'string' || typeof c.tr.en !== 'string') return c;
        const other: DocLang = to === 'th' ? 'en' : 'th';
        if (c.t === c.tr[to]) return c;
        if (c.t !== c.tr[other]) { kept++; return c; }
        changed++; touched = true;
        return { ...c, t: c.tr[to] };
      }));
      if (!touched) return o;
      tables.push({ page, index });
      return { ...o, table: { ...o.table, cells } };
    }
    return o;
  }));
  return { pages: out, changed, kept, tables };
}

/**
 * where a logo goes in its box (fitted inside, not cropped, in the middle). `o` is what is there now: the empty slot
 * (its top-left = the box's) or a logo put in before (in the box's middle) — so another company's logo takes the same box.
 */
export function logoPlace(o: { left?: unknown; top?: unknown; width?: unknown; height?: unknown; scaleX?: unknown; scaleY?: unknown },
  box: [number, number], natW: number, natH: number): { left: number; top: number; scale: number } {
  const [w, h] = box;
  const n = (v: unknown, d: number) => typeof v === 'number' && Number.isFinite(v) ? v : d;
  const x0 = n(o.left, 0) - (w - n(o.width, 0) * n(o.scaleX, 1)) / 2;
  const y0 = n(o.top, 0) - (h - n(o.height, 0) * n(o.scaleY, 1)) / 2;
  const scale = Math.min(w / natW, h / natH);
  return { left: x0 + (w - natW * scale) / 2, top: y0 + (h - natH * scale) / 2, scale };
}
